import { FeatureFlag } from '@/constants/featureFlags';
import { type TemplateFeatures } from '@/constants/templateConfig';
import { type Customer } from '@/types/checkout';
import { PaymentMethodEnum } from '@/types/common';
import { isEmailValid } from '@/utils/email';

/**
 * Whether the email input should be hidden in the current payment form.
 *
 * Order of gates:
 *   1. `CHECKOUT_ENFORCE_CUSTOMER_EMAIL` FF → always hide (BE-owned email).
 *   2. `features.hidePrefilledEmail` template opt-in (PREFERRED / FTD /
 *      last-payment, ACDEV-27034) → hide only when the persisted
 *      `customer.email` is `isEmailValid`. An invalid persisted email still
 *      surfaces the field so the shopper can correct it before the one-tap
 *      CTA fires.
 *   3. Otherwise → show the field.
 *
 * NB — the `customer` parameter and a `|| Boolean(customer?.email)` branch
 * were introduced in PR #3 (Nov 2025) but no production caller has passed
 * `customer` since Jan 2026, so the pre-ACDEV-27034 in-production behaviour
 * was effectively "hide iff the FF is on". This function preserves that on
 * templates that don't opt into `hidePrefilledEmail`.
 *
 * Takes the whole `TemplateFeatures` (rather than a single boolean) to stay
 * consistent with `shouldShowApmDisclaimer` below and to keep the callsite
 * stable when future template-driven email gates are added — only the util
 * changes, not the 12 payment forms.
 */
export const shouldHideEmail = (
  featureFlags: Record<FeatureFlag, boolean>,
  customer?: Customer,
  features?: TemplateFeatures
) => {
  if (featureFlags[FeatureFlag.CHECKOUT_ENFORCE_CUSTOMER_EMAIL]) {
    return true;
  }
  if (features?.hidePrefilledEmail) {
    return isEmailValid(customer?.email ?? '');
  }
  return false;
};

export const shouldShowLoader = (featureFlags: Record<FeatureFlag, boolean>) => {
  return !featureFlags[FeatureFlag.CHECKOUT_DISABLE_PROCESSING_SCREEN];
};

export const shouldShowSuccessScreen = (featureFlags: Record<FeatureFlag, boolean>) => {
  return !featureFlags[FeatureFlag.CHECKOUT_DISABLE_SUCCESS_SCREEN];
};

export const shouldShowSuccessBackButton = (featureFlags: Record<FeatureFlag, boolean>) => {
  return !featureFlags[FeatureFlag.CHECKOUT_HIDE_BACK_TO_GAME_BUTTON];
};

/**
 * When the success screen is disabled, the checkout must still close for hosted
 * flows. Redirect flows (Payment Links, `?mode=redirect`) own the whole tab, so
 * there is no SDK host to post `CLOSE_CHECKOUT` to — they navigate back to the
 * merchant instead and must not be closed here.
 */
export const shouldCloseCheckoutOnHiddenSuccessScreen = (
  featureFlags: Record<FeatureFlag, boolean>,
  isRedirectFlow: boolean
) => !shouldShowSuccessScreen(featureFlags) && !isRedirectFlow;

/**
 * `isRedirectMode` is the `?mode=redirect` param rather than the wider
 * `isRedirectFlow`: Payment Links keeps its X (and the `status=cancel` it
 * emits), while a store-initiated full-page checkout drops it — there is no
 * overlay to close and no page behind it to return to.
 */
export const shouldHideXButton = (featureFlags: Record<FeatureFlag, boolean>, isRedirectMode = false) => {
  return featureFlags[FeatureFlag.CHECKOUT_DEEPLINKS_DISABLE_STORE] || isRedirectMode;
};

/**
 * When on, the "Total" row is folded into the collapsible breakdown instead of
 * always showing in the Details header — so a collapsed header shows only the
 * offer name + "Details" toggle. Applies across all templates.
 */
export const shouldCollapseTotal = (featureFlags: Record<FeatureFlag, boolean>) => {
  return Boolean(featureFlags?.[FeatureFlag.CHECKOUT_COLLAPSE_TOTAL]);
};

/**
 * When on, the Details section starts EXPANDED — the product list, coupon and
 * subtotal/tax are visible without tapping the "Details" toggle.
 *
 * Publisher opt-in that overrides the per-template `defaultDetailsOpen` and the
 * landscape / raw-card / PREFERRED-returning-card collapse exceptions, but NOT
 * a host `defaultOpen` prop. See `resolveDetailsDefaultOpen` in `Details.tsx`
 * for the full precedence order. Off/absent changes nothing.
 */
export const shouldExpandDetails = (featureFlags: Record<FeatureFlag, boolean>) => {
  return Boolean(featureFlags?.[FeatureFlag.CHECKOUT_EXPANDED_DETAILS]);
};

// Card renders its own "Pay with … / Secure and encrypted" block inside
// CreditCardForm, so it's ALWAYS exempt here — otherwise the disclaimer would
// appear twice at the top level.
const CARD_DISCLAIMER_EXEMPT_METHODS = new Set<PaymentMethodEnum>([PaymentMethodEnum.CREDIT_CARD]);

// Wallet methods (Apple Pay / Google Pay / PayPal) render a single branded
// native CTA that already implies a secure, provider-hosted flow — the
// "Secure and encrypted" disclaimer adds nothing there, so it's suppressed for
// them (as APM tiles and in the FTD / last-payment express templates). The one
// exception is when the wallet is surfaced as one of the top payment-method
// boxes: there it stands next to the other primary methods and gets the same
// trust block (see `isTopBoxMethod`).
const WALLET_DISCLAIMER_EXEMPT_METHODS = new Set<PaymentMethodEnum>([
  PaymentMethodEnum.APPLE_PAY,
  PaymentMethodEnum.GOOGLE_PAY,
  PaymentMethodEnum.PAYPAL,
]);

export const shouldShowApmDisclaimer = (
  paymentMethodSelected: PaymentMethodEnum,
  features: TemplateFeatures,
  isTopBoxMethod = false,
  isUnavailable = false
) => {
  if (!features.showApmFormHeader || isUnavailable) {
    return false;
  }
  if (CARD_DISCLAIMER_EXEMPT_METHODS.has(paymentMethodSelected)) {
    return false;
  }
  if (WALLET_DISCLAIMER_EXEMPT_METHODS.has(paymentMethodSelected)) {
    return isTopBoxMethod;
  }
  return true;
};

const PAY_WITH_HEADER_EXEMPT_METHODS = new Set<PaymentMethodEnum>([
  PaymentMethodEnum.CREDIT_CARD,
  PaymentMethodEnum.OTHER,
]);

export const shouldShowPayWithHeader = (
  paymentMethodSelected: PaymentMethodEnum,
  features: TemplateFeatures,
  isTopBoxMethod = false,
  isPaymentMethodUnavailable = false
) =>
  !isPaymentMethodUnavailable &&
  isTopBoxMethod &&
  features.showApmFormHeader &&
  !PAY_WITH_HEADER_EXEMPT_METHODS.has(paymentMethodSelected);
